// Who is The Spy? (Undercover / Mafia) Server Engine
export class SpyEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.wordPairs = [
      { civilian: 'Coffee', spy: 'Tea', civHints: ['Hot morning drink', 'Brewed from beans', 'Popular at cafes', 'Has caffeine'], spyHints: ['Brewed from leaves', 'Often with milk and sugar', 'Popular in Britain and India', 'Green or Black variety'] },
      { civilian: 'Spider-Man', spy: 'Batman', civHints: ['Swings through buildings', 'Red and blue costume', 'Bitten by an insect', 'Shoots webs'], spyHints: ['Protects Gotham', 'Drives a Batmobile', 'Has a butler named Alfred', 'Wears black cape'] },
      { civilian: 'Bicycle', spy: 'Motorcycle', civHints: ['Two wheels', 'Powered by legs', 'Good for exercise', 'Has pedals and chain'], spyHints: ['Has an engine', 'Runs on fuel', 'Can go very fast on highways', 'Requires a helmet'] },
      { civilian: 'YouTube', spy: 'TikTok', civHints: ['Popular video platform', 'Has subscriptions and channels', 'Long format tutorials and vlogs', 'Owned by Google'], spyHints: ['Short vertical videos', 'Viral dance trends and sounds', 'Infinite scroll feed', 'Very popular on smartphones'] },
      { civilian: 'Football', spy: 'Basketball', civHints: ['Played on large grass field', 'Goalposts with nets', 'Dribbling with feet', 'FIFA World Cup'], spyHints: ['Played on wooden court', 'Dribbling with hands', 'Shooting into high hoop', 'NBA tournament'] },
      { civilian: 'Pizza', spy: 'Burger', civHints: ['Round with slices', 'Topped with melted cheese', 'Baked in an oven', 'Italian origin'], spyHints: ['Served inside a bun', 'Meat patty and lettuce', 'Fast food classic', 'Often served with french fries'] }
    ];

    this.phase = 'SPEAKING';
    this.currentSpeakerIndex = 0;
    this.descriptions = [];
    this.votes = {};
    this.eliminatedIds = new Set();
    this.round = 1;
    this.timerInterval = null;
    this.phaseTimer = 30;
    this.playersList = [];
  }

  start() {
    this.eliminatedIds.clear();
    this.round = 1;
    this.descriptions = [];
    this.votes = {};

    // Clone players and add AI bots if fewer than 3 players
    this.playersList = this.room.players.map(p => ({ id: p.id, name: p.name, avatar: p.avatar, isBot: !!p.isBot }));

    if (this.playersList.length < 3) {
      const bots = [
        { id: 'bot_alpha', name: 'Alex 🤖', avatar: '🤖', isBot: true },
        { id: 'bot_beta', name: 'Maya 🤖', avatar: '🐱', isBot: true },
        { id: 'bot_gamma', name: 'Sam 🤖', avatar: '🦊', isBot: true }
      ];
      while (this.playersList.length < 3 && bots.length > 0) {
        this.playersList.push(bots.shift());
      }
    }

    // Pick pair & assign roles
    this.activePair = this.wordPairs[Math.floor(Math.random() * this.wordPairs.length)];
    const spyIndex = Math.floor(Math.random() * this.playersList.length);

    this.playerRoles = {};
    this.playersList.forEach((p, idx) => {
      const isSpy = idx === spyIndex;
      const word = isSpy ? this.activePair.spy : this.activePair.civilian;
      const hints = isSpy ? this.activePair.spyHints : this.activePair.civHints;
      this.playerRoles[p.id] = { ...p, isSpy, word, hints, isAlive: true };

      if (!p.isBot) {
        this.io.to(p.id).emit('spy_secretWord', { word, isSpy });
      }
    });

    this.io.to(this.room.code).emit('spy_gameReady', {
      players: this.playersList
    });

    setTimeout(() => this.startSpeakingRound(), 1500);
  }

  startSpeakingRound() {
    this.phase = 'SPEAKING';
    this.currentSpeakerIndex = 0;
    this.findNextAliveSpeaker();

    const activePlayers = this.playersList.filter((p) => !this.eliminatedIds.has(p.id));
    const currentSpeaker = activePlayers[this.currentSpeakerIndex];

    this.io.to(this.room.code).emit('spy_turnPrompt', {
      speakerId: currentSpeaker.id,
      speakerName: currentSpeaker.name,
      round: this.round,
      aliveCount: activePlayers.length
    });

    if (currentSpeaker.isBot) {
      setTimeout(() => this.botSpeak(currentSpeaker), 2000);
    }
  }

  botSpeak(bot) {
    const role = this.playerRoles[bot.id];
    const hint = role.hints[Math.floor(Math.random() * role.hints.length)];
    this.submitDescription(bot.id, hint);
  }

  submitDescription(playerId, text) {
    const activePlayers = this.playersList.filter((p) => !this.eliminatedIds.has(p.id));
    const currentSpeaker = activePlayers[this.currentSpeakerIndex];
    if (!currentSpeaker || currentSpeaker.id !== playerId) return;

    this.descriptions.push({ id: playerId, name: currentSpeaker.name, text: text.trim() });
    this.io.to(this.room.code).emit('spy_newDescription', {
      id: playerId,
      name: currentSpeaker.name,
      text: text.trim()
    });

    this.currentSpeakerIndex++;
    if (this.currentSpeakerIndex >= activePlayers.length) {
      setTimeout(() => this.startVotingPhase(), 1500);
    } else {
      const nextSpeaker = activePlayers[this.currentSpeakerIndex];
      this.io.to(this.room.code).emit('spy_turnPrompt', {
        speakerId: nextSpeaker.id,
        speakerName: nextSpeaker.name,
        round: this.round,
        aliveCount: activePlayers.length
      });

      if (nextSpeaker.isBot) {
        setTimeout(() => this.botSpeak(nextSpeaker), 2200);
      }
    }
  }

  startVotingPhase() {
    this.phase = 'VOTING';
    this.votes = {};
    this.phaseTimer = 20;

    const alivePlayers = this.playersList.filter((p) => !this.eliminatedIds.has(p.id));
    this.io.to(this.room.code).emit('spy_votingStarted', {
      alivePlayers: alivePlayers.map((p) => ({ id: p.id, name: p.name, avatar: p.avatar })),
      timeLimit: this.phaseTimer
    });

    // Bot automatic voting
    alivePlayers.filter(p => p.isBot).forEach((bot) => {
      const candidates = alivePlayers.filter(c => c.id !== bot.id);
      const randomTarget = candidates[Math.floor(Math.random() * candidates.length)];
      setTimeout(() => {
        if (this.phase === 'VOTING') {
          this.castVote(bot.id, randomTarget.id);
        }
      }, 1500 + Math.random() * 2500);
    });

    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.phaseTimer--;
      this.io.to(this.room.code).emit('spy_tick', { timer: this.phaseTimer });

      if (this.phaseTimer <= 0 || Object.keys(this.votes).length >= alivePlayers.length) {
        this.tallyVotes();
      }
    }, 1000);
  }

  castVote(voterId, targetId) {
    if (this.eliminatedIds.has(voterId)) return;
    this.votes[voterId] = targetId;

    const aliveCount = this.playersList.filter((p) => !this.eliminatedIds.has(p.id)).length;
    this.io.to(this.room.code).emit('spy_voteUpdate', {
      votesCount: Object.keys(this.votes).length,
      totalAlive: aliveCount
    });

    if (Object.keys(this.votes).length >= aliveCount) {
      this.tallyVotes();
    }
  }

  tallyVotes() {
    if (this.timerInterval) clearInterval(this.timerInterval);

    const counts = {};
    for (const voterId in this.votes) {
      const target = this.votes[voterId];
      counts[target] = (counts[target] || 0) + 1;
    }

    let highestId = null;
    let maxVotes = 0;
    for (const target in counts) {
      if (counts[target] > maxVotes) {
        maxVotes = counts[target];
        highestId = target;
      }
    }

    if (highestId) {
      this.eliminatedIds.add(highestId);
      const roleInfo = this.playerRoles[highestId];
      if (roleInfo) roleInfo.isAlive = false;

      this.io.to(this.room.code).emit('spy_playerEliminated', {
        id: highestId,
        name: roleInfo?.name || 'Player',
        isSpy: roleInfo?.isSpy,
        word: roleInfo?.word,
        votes: maxVotes
      });

      // Win Conditions
      if (roleInfo?.isSpy) {
        setTimeout(() => {
          this.io.to(this.room.code).emit('spy_gameOver', {
            winner: 'CIVILIANS',
            message: `🎉 Civilians caught the Spy (${roleInfo.name})! The spy word was "${roleInfo.word}".`
          });
        }, 3500);
        return;
      }

      const remainingAlive = this.playersList.filter((p) => !this.eliminatedIds.has(p.id));
      if (remainingAlive.length <= 2) {
        const spy = this.playersList.find(p => this.playerRoles[p.id]?.isSpy);
        setTimeout(() => {
          this.io.to(this.room.code).emit('spy_gameOver', {
            winner: 'SPY',
            message: `🕵️ The Spy (${spy?.name}) infiltrated the party and survived!`
          });
        }, 3500);
        return;
      }
    }

    this.round++;
    setTimeout(() => this.startSpeakingRound(), 3500);
  }

  findNextAliveSpeaker() {
    const active = this.playersList.filter((p) => !this.eliminatedIds.has(p.id));
    if (this.currentSpeakerIndex >= active.length) {
      this.currentSpeakerIndex = 0;
    }
  }

  destroy() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }
}
